import React, { useEffect, useState } from 'react';
import { Navigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../api/admin';

export default function AdminUserDetailPage() {
  const { id } = useParams();
  const { user, accessToken, loading } = useAuth();

  const [data, setData]       = useState(null);
  const [busy, setBusy]       = useState(true);
  const [error, setError]     = useState('');
  const [tiers, setTiers]     = useState(['Free', 'NoAds', 'Plus', 'Pro']);
  const [tierSaving, setTierSaving] = useState(false);
  const [tierMsg, setTierMsg] = useState('');
  const [maxSaving, setMaxSaving] = useState(false);
  const [maxMsg, setMaxMsg] = useState('');

  useEffect(() => {
    if (!accessToken || !user || user.role !== 'Admin') return;
    (async () => {
      try {
        const [d, t] = await Promise.all([
          adminApi.userDetail(accessToken, id),
          adminApi.tiers(accessToken).catch(() => null),
        ]);
        setData(d);
        if (Array.isArray(t) && t.length) setTiers(t);
      } catch (err) {
        setError(err.message || 'Failed to load user.');
      } finally {
        setBusy(false);
      }
    })();
  }, [accessToken, user, id]);

  async function handleMaxChange(newVal) {
    const v = Math.max(1, Math.min(1000, parseInt(newVal, 10) || 0));
    if (!data || v === data.user.maxPortfolios) return;
    setMaxSaving(true);
    setMaxMsg('');
    try {
      await adminApi.setMaxPortfolios(accessToken, id, v);
      setData(prev => ({ ...prev, user: { ...prev.user, maxPortfolios: v } }));
      setMaxMsg(`Max portfolios updated to ${v}.`);
      setTimeout(() => setMaxMsg(''), 2500);
    } catch (err) {
      setMaxMsg(`Failed: ${err.message}`);
      setTimeout(() => setMaxMsg(''), 4000);
    } finally {
      setMaxSaving(false);
    }
  }

  async function handleTierChange(newTier) {
    if (!data || newTier === data.user.tier) return;
    setTierSaving(true);
    setTierMsg('');
    try {
      const res = await adminApi.setTier(accessToken, id, newTier);
      setData(prev => ({
        ...prev,
        user: {
          ...prev.user,
          tier: newTier,
          maxPortfolios: res?.maxPortfolios ?? prev.user.maxPortfolios,
        },
      }));
      setTierMsg(`Tier updated to ${newTier}.`);
      setTimeout(() => setTierMsg(''), 2500);
    } catch (err) {
      setTierMsg(`Failed: ${err.message}`);
      setTimeout(() => setTierMsg(''), 4000);
    } finally {
      setTierSaving(false);
    }
  }

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'Admin') return <Navigate to="/" replace />;

  return (
    <div style={s.wrap}>
      <Link to="/admin" style={s.back}>← Back to Admin Tools</Link>

      {busy ? (
        <div style={s.muted}>Loading…</div>
      ) : error ? (
        <div style={s.error}>{error}</div>
      ) : data ? (
        <>
          <h1 style={s.h1}>{data.user.email}</h1>
          <p style={s.sub}>
            User #{data.user.id} · Role <strong>{data.user.role}</strong> · {data.user.isActive ? 'Active' : 'Inactive'}
            {data.user.hasPendingRegistration && ' · pending invite'}
          </p>

          <section style={s.card}>
            <h2 style={s.h2}>Account</h2>
            <div style={s.kv}><span>Email</span><strong>{data.user.email}</strong></div>
            <div style={s.kv}><span>Role</span><strong>{data.user.role}</strong></div>
            <div style={s.kv}><span>Active</span><strong>{data.user.isActive ? '✓' : '—'}</strong></div>
            <div style={s.kv}>
              <span>Max portfolios</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="number"
                  min={1}
                  max={1000}
                  defaultValue={data.user.maxPortfolios}
                  onBlur={(e) => handleMaxChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  disabled={maxSaving}
                  style={s.numberInput}
                />
                {maxSaving && <span style={s.muted}>saving…</span>}
              </span>
            </div>
            {maxMsg && <div style={s.note}>{maxMsg}</div>}
            <div style={s.kv}>
              <span>Tier</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <select
                  value={data.user.tier || 'Free'}
                  onChange={(e) => handleTierChange(e.target.value)}
                  disabled={tierSaving}
                  style={s.tierSelect}
                >
                  {tiers.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                {tierSaving && <span style={s.muted}>saving…</span>}
              </span>
            </div>
            {tierMsg && <div style={s.note}>{tierMsg}</div>}
            <div style={s.kv}><span>Created</span><strong>{new Date(data.user.createdAt).toLocaleString()}</strong></div>
          </section>

          <section style={s.card}>
            <h2 style={s.h2}>Activity</h2>
            <div style={s.statsGrid}>
              <Stat label="Portfolios"        value={data.stats.portfolios} />
              <Stat label="Transactions"      value={data.stats.totalTransactions} />
              <Stat label="Buys"              value={data.stats.totalBuy} />
              <Stat label="Sells"             value={data.stats.totalSell} />
              <Stat label="Dividends"         value={data.stats.totalDividends} />
            </div>
          </section>

          <section style={s.card}>
            <h2 style={s.h2}>Portfolios</h2>
            {data.portfolios.length === 0 ? (
              <div style={s.muted}>No portfolios.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>ID</th>
                      <th style={s.th}>Name</th>
                      <th style={s.th}>Currency</th>
                      <th style={s.th}>Tx</th>
                      <th style={s.th}>Buy</th>
                      <th style={s.th}>Sell</th>
                      <th style={s.th}>Div</th>
                      <th style={s.th}>Tickers</th>
                      <th style={s.th}>First trade</th>
                      <th style={s.th}>Last trade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.portfolios.map(p => (
                      <tr key={p.id}>
                        <td style={s.td}>{p.id}</td>
                        <td style={s.td}>{p.name}</td>
                        <td style={s.td}>{p.baseCurrency}</td>
                        <td style={s.td}>{p.transactionCount}</td>
                        <td style={s.td}>{p.buyCount}</td>
                        <td style={s.td}>{p.sellCount}</td>
                        <td style={s.td}>{p.dividendCount}</td>
                        <td style={s.td}>{p.holdings}</td>
                        <td style={s.td}>{p.firstTradeAt ? new Date(p.firstTradeAt).toLocaleDateString() : '—'}</td>
                        <td style={s.td}>{p.lastTradeAt  ? new Date(p.lastTradeAt).toLocaleDateString()  : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={s.statBox}>
      <div style={s.statLabel}>{label}</div>
      <div style={s.statValue}>{value ?? '—'}</div>
    </div>
  );
}

const s = {
  wrap:        { maxWidth: '1100px', margin: '0 auto', padding: '32px 24px', color: 'var(--text-primary)' },
  back:        { display: 'inline-block', color: 'var(--accent)', textDecoration: 'none', fontSize: '14px', fontWeight: 600, marginBottom: '16px' },
  h1:          { fontSize: '28px', fontWeight: 700, margin: '0 0 4px', wordBreak: 'break-all' },
  sub:         { color: 'var(--text-secondary)', fontSize: '14px', margin: '0 0 24px' },
  card:        { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px 22px', marginBottom: '20px', boxShadow: 'var(--shadow)' },
  h2:          { fontSize: '18px', fontWeight: 700, margin: '0 0 12px' },
  muted:       { color: 'var(--text-secondary)', fontSize: '14px' },
  error:       { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px', padding: '12px 14px', color: '#ef4444', fontSize: '14px' },
  kv:          { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '14px', gap: '12px' },
  statsGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' },
  statBox:     { background: 'var(--surface-strong)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 14px' },
  statLabel:   { fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' },
  statValue:   { fontSize: '22px', fontWeight: 700 },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th:          { textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' },
  td:          { padding: '10px 8px', borderBottom: '1px solid var(--border)' },
  tierSelect:  { padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface-strong)', color: 'var(--text-primary)', fontWeight: 700, fontSize: '14px', cursor: 'pointer' },
  numberInput: { width: '80px', padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface-strong)', color: 'var(--text-primary)', fontWeight: 700, fontSize: '14px', textAlign: 'right' },
  note:        { marginTop: '8px', padding: '8px 10px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '8px', fontSize: '13px' },
};
